import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, Pencil, Plus } from 'lucide-react';
import { UserRole, type UserRole as UserRoleCode } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { EMPTY, enumOptions } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { SkeletonLines } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/components/ui/toaster';
import { AdminOnly, SettingsSection } from '@/pages/settings/SettingsSection';
import { unwrapList } from '@/pages/settings/useSettings';

export interface UserRow {
  id: string;
  email: string;
  name: string;
  /** Job title for the contact block of generated documents; null when none. */
  title?: string | null;
  role: UserRoleCode;
  active: boolean;
  scopes?: string[];
  kind?: string;
}

const USERS_KEY = ['users'] as const;

const PASSWORD_MIN = 10;
const TITLE_MAX = 80;

const createSchema = z.object({
  email: z.string().trim().min(1, 'E-mail is required').email('Enter a valid e-mail'),
  name: z.string().trim().min(1, 'Name is required').max(120),
  title: z.string().trim().max(TITLE_MAX, `At most ${TITLE_MAX} characters`),
  role: UserRole.schema,
  password: z.string().min(PASSWORD_MIN, `At least ${PASSWORD_MIN} characters`),
});
type CreateValues = z.infer<typeof createSchema>;

/** Users & roles (spec §4.5). The API is admin-only, so other roles only see a note. */
export function UsersSettings() {
  return (
    <SettingsSection
      title="Users & roles"
      description="Who can sign in and what they may do. Viewers are read-only."
    >
      <AdminOnly what="users and roles">
        <UsersAdmin />
      </AdminOnly>
    </SettingsSection>
  );
}

function UsersAdmin() {
  const { user: me, reload } = useAuth();
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => unwrapList<UserRow>(await api.get<unknown>('/users')),
  });
  const [createOpen, setCreateOpen] = React.useState(false);
  const [resetFor, setResetFor] = React.useState<UserRow | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: USERS_KEY });

  const patch = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<Pick<UserRow, 'name' | 'title' | 'role' | 'active'>> & { password?: string };
    }) => api.patch<UserRow>(`/users/${id}`, body),
    onSuccess: async (_u, vars) => {
      await refresh();
      // The Account page and the documents read the title of the signed-in user.
      if (vars.id === me?.id && vars.body.title !== undefined) await reload().catch(() => undefined);
      toast.success(vars.body.password ? 'Password reset' : 'User updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" /> New user
        </Button>
      </div>

      {users.isLoading ? (
        <SkeletonLines lines={4} />
      ) : users.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Users could not be loaded: {users.error.message}
        </p>
      ) : users.data && users.data.length === 0 ? (
        <EmptyState compact title="No users" description="Create the first user." />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <caption className="sr-only">Users</caption>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Password</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(users.data ?? []).map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      {u.name}
                      {isMe ? (
                        <Badge variant="outline" className="ml-2 font-normal">
                          you
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <TitleCell
                        user={u}
                        disabled={patch.isPending}
                        onCommit={(title) => patch.mutate({ id: u.id, body: { title } })}
                      />
                    </TableCell>
                    <TableCell>
                      <a href={`mailto:${u.email}`} className="underline-offset-4 hover:underline">
                        {u.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <SimpleSelect
                        aria-label={`Role of ${u.name}`}
                        value={u.role}
                        options={enumOptions(UserRole)}
                        onChange={(v) => {
                          if (v && v !== u.role)
                            patch.mutate({ id: u.id, body: { role: v as UserRoleCode } });
                        }}
                        disabled={patch.isPending}
                        className="h-8 w-36"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={u.active}
                          aria-label={`${u.name} is ${u.active ? 'active' : 'inactive'}`}
                          disabled={isMe || patch.isPending}
                          onCheckedChange={(active) => patch.mutate({ id: u.id, body: { active } })}
                        />
                        <span className="text-xs text-muted-foreground">
                          {u.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setResetFor(u)}>
                        <KeyRound aria-hidden="true" /> Reset
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {users.data && users.data.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          The title is shown next to the name on generated documents, e.g. Co-Founder. Click a title to change
          it; leave it empty to remove it.
        </p>
      ) : null}

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={refresh} />
      <ResetPasswordDialog
        user={resetFor}
        onOpenChange={(open) => {
          if (!open) setResetFor(null);
        }}
        onSubmit={async (password) => {
          if (resetFor) await patch.mutateAsync({ id: resetFor.id, body: { password } });
        }}
      />
    </div>
  );
}

/** Click-to-edit title of one user. Enter/blur saves, Esc cancels; an empty value clears it (`null`). */
function TitleCell({
  user,
  disabled,
  onCommit,
}: {
  user: UserRow;
  disabled: boolean;
  onCommit: (title: string | null) => void;
}) {
  const current = user.title?.trim() || null;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  // Enter/Esc unmount the input, which may still fire a blur: close exactly once.
  const open = React.useRef(false);

  const close = (save: boolean) => {
    if (!open.current) return;
    open.current = false;
    setEditing(false);
    if (!save) return;
    const next = draft.trim() || null;
    if (next !== current) onCommit(next);
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        maxLength={TITLE_MAX}
        placeholder="e.g. Co-Founder"
        aria-label={`Title of ${user.name}`}
        className="h-8 min-w-[10rem]"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => close(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            close(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            close(false);
          }
        }}
      />
    );
  }
  return (
    <button
      type="button"
      aria-label={`Edit title of ${user.name}`}
      title={current ?? undefined}
      className="group -mx-1.5 inline-flex max-w-[16rem] items-center gap-1.5 rounded px-1.5 py-0.5 text-left hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      disabled={disabled}
      onClick={() => {
        setDraft(current ?? '');
        open.current = true;
        setEditing(true);
      }}
    >
      <span className={cn('truncate', !current && 'text-muted-foreground')}>{current ?? EMPTY}</span>
      <Pencil
        className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden="true"
      />
    </button>
  );
}

function CreateUserDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => unknown;
}) {
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { email: '', name: '', title: '', role: 'RECRUITER', password: '' },
  });
  const { reset, setError } = form;
  React.useEffect(() => {
    if (open) reset({ email: '', name: '', title: '', role: 'RECRUITER', password: '' });
  }, [open, reset]);

  const create = useMutation({
    // The title is optional: it is sent only when one was entered.
    mutationFn: ({ title, ...values }: CreateValues) =>
      api.post<UserRow>('/users', title ? { ...values, title } : values),
    onSuccess: async (u) => {
      await onCreated();
      toast.success(`User ${u.email} created`);
      onOpenChange(false);
    },
    onError: (err: Error) => {
      if (isApiError(err)) {
        const fields = err.fieldErrors();
        for (const [path, message] of Object.entries(fields)) {
          if (
            path === 'email' ||
            path === 'name' ||
            path === 'title' ||
            path === 'role' ||
            path === 'password'
          )
            setError(path, { message });
        }
        if (err.status === 409) {
          setError('email', { message: err.message });
          return;
        }
        if (Object.keys(fields).length > 0) return;
      }
      toast.error(err.message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>New user</DialogTitle>
              <DialogDescription>
                The user signs in with the e-mail and the password you set here.
              </DialogDescription>
            </DialogHeader>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input autoFocus autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" maxLength={TITLE_MAX} {...field} />
                  </FormControl>
                  <FormDescription>
                    Optional. Shown next to the name on generated documents, e.g. Co-Founder.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <FormControl>
                    <SimpleSelect
                      id={field.name}
                      value={field.value}
                      options={enumOptions(UserRole)}
                      onChange={(v) => field.onChange(v ?? 'RECRUITER')}
                    />
                  </FormControl>
                  <FormDescription>
                    Admins manage settings and users; recruiters work the pipeline; viewers only read.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormDescription>At least {PASSWORD_MIN} characters.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create user
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user,
  onOpenChange,
  onSubmit,
}: {
  user: UserRow | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (password: string) => Promise<void>;
}) {
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const open = user !== null;
  React.useEffect(() => {
    if (open) setPassword('');
  }, [open]);
  const valid = password.length >= PASSWORD_MIN;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!valid) return;
            setBusy(true);
            try {
              await onSubmit(password);
              onOpenChange(false);
            } catch {
              /* toasted by the mutation */
            } finally {
              setBusy(false);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Reset password{user ? ` for ${user.name}` : ''}</DialogTitle>
            <DialogDescription>Existing sessions of this user are signed out.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-password">New password</Label>
            <Input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">At least {PASSWORD_MIN} characters.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!valid}>
              Set password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
